<template>
  <div :class="{ 'sidebar-visible': sidebarVisible }">
    <base-header class="pb-6" />

    <b-container fluid class="mt--6 max-width">
      <b-card no-body>
        <card-header title="Dataviews (All Entities)">
          <div class="d-flex justify-content-end align-items-center">
          <b-button
              @click="toggleSidebar"
              variant="outline-secondary"
              size="sm"
              class="mr-2"
              aria-label="Toggle Filters Sidebar"
              :aria-expanded="sidebarVisible"
            >
              <i class="fas fa-filter"></i>
              Filter
              <i
                :class="
                  sidebarVisible ? 'fas fa-chevron-up' : 'fas fa-chevron-down'
                "
                class="transition-icon"
              ></i>
            </b-button>
          <base-button
            size="sm"
            @click="onNewApp"
            v-if="
              (hasAllAccess || hasAccessto.includes('Dataview_all')) &&
              accountSubtype === 'admin'
            "
            >Add Dataview</base-button
          >
          </div>
        </card-header>
        <transition name="slide-fade">
          <filter-sidebar
            v-if="sidebarVisible"
            ref="filterSidebar"
            :title="'Dataview Filters'"
            :filters-config="dataviewFiltersConfig"
            :visible="sidebarVisible"
            v-model="activeFilters"
            @filter-change="handleFilterChange"
            @filter-reset="handleFilterReset"
          >
          </filter-sidebar>
        </transition>

        <el-table
          header-row-class-name="thead-light"
          :data="paginatedDataviews"
          @selection-change="handleSelectionChange"
          ref="dataviewsTable"
          :row-key="'name'"
          class="table-with-transition"
        >
          <template #empty
            ><span v-if="loading"
              >Loading Dataviews
              <i class="fas fa-sync-alt fa-spin mr-1"></i></span
            ><span v-else>No Dataviews Found</span></template
          >
           <!-- Checkbox Column for selecting rows -->
          <el-table-column
            type="selection"
            width="55"
            :reserve-selection="true"
          >
          </el-table-column>
          <el-table-column
            header-class-name="fixed-header"
            class-name="font-weight-bold"
            label="Name"
            sortable
            prop="name"
          />
           <el-table-column label="Entity" sortable prop="entity" />
          <el-table-column label="Owner" prop="owner" />
          <el-table-column label="Version" prop="version" />
          <el-table-column :render-header="renderAuthorizedAccountsHeader"
            ><div slot-scope="{ row }">
              {{ row.authorized_accounts.length }}
            </div></el-table-column
          >
          <el-table-column label="Description" sortable prop="description" />

          <el-table-column
            width="300px"
            label="Actions"
            v-if="
              (hasAllAccess || hasAccessto.includes('Dataview_all')) &&
              accountSubtype === 'admin' ||  accountSubtype === 'superadmin'
            "
          >
            <div slot-scope="{ row }" class="table-actions">
              <base-button size="sm" @click="onDownload(row)"
                ><i class="fas fa-download"></i
              ></base-button>

              <base-button
                @click.native="onAppBuilder(row)"
                class="remove btn-link"
                type=""
                size="sm"
                icon
              >
                <i class="text-dark fas fa-image"></i>
              </base-button>

              <base-button
                @click.native="onEdit(row)"
                class="remove btn-link"
                type=""
                size="sm"
                icon
              >
                <i class="text-dark fas fa-edit"></i>
              </base-button>

              <base-button
                @click.native="onDelete(row)"
                class="remove btn-link"
                type="danger"
                size="sm"
                icon
              >
                <i class="text-white fas fa-trash"></i>
              </base-button>
            </div>
          </el-table-column>
        </el-table>
         <div class="pagination-container">
          <el-pagination
               @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="currentPage"
                :page-sizes="[10, 15, 20, 50]"
                :page-size="pageSize"
                layout=" prev, pager, next"
                :total="filteredDataviews.length"
                prev-text="Prev"
                next-text="Next"
          >
          </el-pagination>
        </div>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import { Action } from "@/store/actions";
import { mapState, mapGetters } from "vuex";
import { Table, TableColumn, Pagination } from "element-ui";
import CardHeader from "@/views/components/CardHeader";
import { Routes } from "@/router";
import { confirmDelete, download } from "@/helper";
import FilterSidebar from "../../views/components/FilterSidebar.vue";

export default {
  name: "sDataviewList",
  components: {
    CardHeader,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Pagination.name]: Pagination,
    FilterSidebar,
  },
  data() {
    return {
      refreshInterval: null,
      sidebarVisible: false,
      activeFilters: {
        entity: [],
        owner: [],
        version: [],
      },
      selectedDataviews: [],
      persistentSelectedIds: new Set(),
      loading: false,
      dataviewFiltersConfig: [
        {
          id: "entity",
          label: "Entity",
          type: "checkbox",
          options: [],
        },
        {
          id: "owner",
          label: "Owner",
          type: "checkbox",
          options: [],
        },
        {
          id: "version",
          label: "Version",
          type: "checkbox",
          options: [],
        },
      ],
      pageSize: 15, // Set default to 15 as requested
      currentPage: 1,
      completeLoad: false,
    };
  },

  computed: {
    ...mapState({
      dataviews: (state) => state.app.apps,
      total_dataviews: (state) => state.app.totalApps,
      entities: (state) => state.landlord.entity,
    }),
    ...mapGetters(["hasAllAccess", "hasAccessto", "accountSubtype", "activeEntity"]),
    
    filteredDataviews() {
      let filtered = this.dataviews;
      
      // Apply Entity filters
      if (this.activeFilters.entity && this.activeFilters.entity.length) {
        filtered = filtered.filter((dataview) =>
          this.activeFilters.entity.includes(dataview.entity)
        );
      }
      
      // Apply Owner filters
      if (this.activeFilters.owner && this.activeFilters.owner.length) {
        filtered = filtered.filter((dataview) =>
          this.activeFilters.owner.includes(dataview.owner)
        );
      }
      
      // Apply Version filters
      if (this.activeFilters.version && this.activeFilters.version.length) {
        filtered = filtered.filter((dataview) =>
          this.activeFilters.version.includes(dataview.version)
        );
      }
      
      return filtered;
    },
    
    // New computed property for paginated data
    paginatedDataviews() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredDataviews.slice(startIndex, endIndex);
    },
    
    entityOptions() {
      const entities = new Set(this.dataviews.map((dataview) => dataview.entity));
      return Array.from(entities).map((entity) => ({
        value: entity,
        text: entity,
      }));
    },
    
    ownerOptions() {
      const owners = new Set(this.dataviews.map((dataview) => dataview.owner));
      return Array.from(owners).map((owner) => ({
        value: owner,
        text: owner,
      }));
    },
    
    versionOptions() {
      const versions = new Set(this.dataviews.map((dataview) => dataview.version));
      return Array.from(versions).map((version) => ({
        value: version,
        text: version,
      }));
    },
  },
  
  watch: {
    entityOptions: {
      immediate: true,
      handler(newVal) {
        const entityFilterIndex = this.dataviewFiltersConfig.findIndex(
          (f) => f.id === "entity"
        );
        if (entityFilterIndex !== -1) {
          this.dataviewFiltersConfig[entityFilterIndex].options = newVal;
        }
      },
    },
    ownerOptions: {
      immediate: true,
      handler(newVal) {
        const ownerFilterIndex = this.dataviewFiltersConfig.findIndex(
          (f) => f.id === "owner"
        );
        if (ownerFilterIndex !== -1) {
          this.dataviewFiltersConfig[ownerFilterIndex].options = newVal;
        }
      },
    },
    versionOptions: {
      immediate: true,
      handler(newVal) {
        const versionFilterIndex = this.dataviewFiltersConfig.findIndex(
          (f) => f.id === "version"
        );
        if (versionFilterIndex !== -1) {
          this.dataviewFiltersConfig[versionFilterIndex].options = newVal;
        }
      },
    },
    
    // Reset to first page when filters change
    activeFilters: {
      deep: true,
      handler() {
        this.currentPage = 1;
      }
    }
  },
  
  methods: {
    handleSizeChange(newSize) {
      this.pageSize = newSize;
      this.currentPage = 1; // Reset to first page when changing page size
    },
    
    handleCurrentChange(newPage) {
    const totalPages = Math.ceil(this.filteredDataviews.length / this.pageSize);
    if (newPage > totalPages) {
        this.currentPage = totalPages;
    } else {
        this.currentPage = newPage;
    }
},
    
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    
    async loadDataviews() {
      this.loading = true;
      try {
        await this.$store.dispatch(Action.app.load_all, { limit: 0, offset: 0});
      } catch (error) {
        console.error("Failed to load dataviews:", error);
      } finally {
        this.loading = false;
      }
    },
    
    async loadDataviewsAll() {
      try {
        await this.$store.dispatch(Action.app.load_all, { limit: 0, offset: 0 });
      } catch (error) {
        console.error("Failed to load all dataviews:", error);
      }
    },
    
    handleSelectionChange(selectedRows) {
      this.selectedDataviews = selectedRows;
      this.updatePersistentSelection();
    },
    
    updatePersistentSelection() {
      this.persistentSelectedIds = new Set(
        this.selectedDataviews.map((dataview) => dataview.name)
      );
    },
    
    handleFilterChange({ filterId, value }) {
      this.activeFilters[filterId] = value;
    },
    
    onDelete(row) {
      confirmDelete().then((result) => {
        if (result.value) {
          this.$store.dispatch(Action.app.delete, row);
        }
      });
    },
    
    onNewApp() {
      this.$router.push({ name: Routes.appNew });
    },
    
    onEdit(row) {
      this.$router.push({ name: Routes.appEdit, params: { name: row.name } });
    },
    
    onAppBuilder(row) {
      const url = `https://dataviewdesigner.infrafon.club?dataview_id=${encodeURIComponent(row.name)}`;
      window.open(url, "_blank");
    },
    
    async onDownload(row) {
    try {
        // Fetch the full dataview content
        const dataviewWithContent = await this.$store.dispatch(Action.app.get, { 
            entity: row.entity, 
            name: row.name 
        })
        
        // Download the content
        download(`${row.name}.json`, JSON.stringify(dataviewWithContent.content))
    } catch (error) {
        console.error('Failed to download dataview:', error)
        this.$notify({
            type: 'error',
            message: 'Failed to download dataview'
        })
    }
},
    
    renderAuthorizedAccountsHeader() {
      return (
        <span>
          Authorized
          <br />
          Accounts
        </span>
      );
    },
  },
  
  mounted() {
    this.loadDataviews();
    this.completeLoad = true;
    
    this.refreshInterval = setInterval(() => {
      if (this.completeLoad === true) {
        this.loadDataviewsAll();
        this.completeLoad = false;
      } else {
        this.loadDataviews();
      }
    }, 30000);
  },
  
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
};
</script>

<style>
.sidebar-visible {
  /* Sidebar visibility styles */
}

.table-with-transition {
  /* Table transition styles */
}

.fixed-header {
  position: sticky;
  top: 0;
  background-color: #f5f7fa;
  z-index: 3;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: max-height 0.5s ease-in-out, transform 0.3s ease-in-out,
    opacity 0.2s ease-in-out;
  overflow: hidden;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(100%);
  opacity: 0;
  max-height: 0;
}

.slide-fade-enter-to,
.slide-fade-leave {
  transform: translateY(0);
  opacity: 1;
  max-height: 500px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  padding: 15px;
}
</style>